import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiClientError } from '../lib/api';
import { ErrorNotice } from './Layout';

interface PlaudRecording {
  id: string;
  name: string;
  createdAt: string | null;
  durationMs: number | null;
  imported: boolean;
}

interface PlaudImport {
  id: string;
  recordingId: string;
  recordingName: string;
  recordedAt: string | null;
  durationMs: number | null;
  storedFileId: string | null;
  summary: string | null;
  hasTranscript: boolean;
  status: 'importing' | 'needs_review' | 'confirmed' | 'failed';
  errorMessage: string | null;
}
type PlaudStatus = 'connected' | 'token_missing' | 'token_unreadable' | 'token_invalid' | 'token_not_writable' | 'auth_failed' | 'cli_missing' | 'service_unavailable';
const plaudStatusMessage: Record<PlaudStatus, string> = {
  connected: 'เชื่อมต่อ PLAUD สำเร็จ ลองโหลดรายการอีกครั้ง',
  token_missing: 'ไม่พบไฟล์ tokens.json ใน volume /plaud-home/.plaud ของ gateway บน NAS',
  token_unreadable: 'gateway อ่านไฟล์ tokens.json ไม่ได้ ตรวจสิทธิ์และเจ้าของไฟล์บน NAS',
  token_invalid: 'ไฟล์ tokens.json ไม่ใช่ JSON ที่ถูกต้อง ต้องเข้าสู่ระบบ PLAUD ใหม่',
  token_not_writable: 'gateway เขียนไฟล์ tokens.json ไม่ได้ ทำให้ต่ออายุ token ไม่สำเร็จ ตรวจสิทธิ์ไฟล์บน NAS',
  auth_failed: 'PLAUD ปฏิเสธ token ที่เก็บบน NAS ต้องเข้าสู่ระบบ PLAUD CLI ใหม่แล้วอัปเดตไฟล์ token',
  cli_missing: 'ไม่พบ PLAUD CLI ใน container gateway ตรวจ package และ build image ใหม่',
  service_unavailable: 'CLI หรือบริการ PLAUD ตอบกลับไม่สำเร็จ ตรวจเครือข่าย NAS และ log ของ gateway',
};

function duration(value: number | null): string {
  if (value === null) return '-';
  const totalMinutes = Math.round(value / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours ? `${hours} ชม. ${minutes} นาที` : `${minutes} นาที`;
}

function date(value: string | null): string {
  if (!value) return 'ไม่ระบุวันที่';
  const parsed = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: value.length === 10 ? undefined : 'short' });
}

export function PlaudImportPanel({ meetingId }: { meetingId: string }) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selectedSummaries, setSelectedSummaries] = useState<string[]>([]);
  const [mergePrompt, setMergePrompt] = useState('รวม Summary ต่อไปนี้เป็นสรุปการประชุมภาษาไทยที่กระชับ แยกหัวข้อสำคัญ มติ และงานติดตาม โดยใช้เฉพาะข้อมูลที่ให้มา ห้ามสร้างข้อมูลใหม่');
  const [refreshMessage, setRefreshMessage] = useState('');
  const recordings = useQuery({
    queryKey: ['plaud-recordings'],
    queryFn: () => api<{ data: PlaudRecording[] }>('/api/plaud/recordings?page=1&pageSize=20').then((result) => result.data),
    enabled: open,
    retry: false,
  });
  const diagnosis = useQuery({ queryKey: ['plaud-status'], queryFn: () => api<{ data: { status: PlaudStatus } }>('/api/plaud/status').then((r) => r.data), enabled: false, retry: false });
  const imports = useQuery({
    queryKey: ['plaud-imports', meetingId],
    queryFn: () => api<{ data: PlaudImport[] }>(`/api/meetings/${meetingId}/plaud-imports`).then((result) => result.data),
  });
  const importRecording = useMutation({
    mutationFn: (recordingId: string) => api(`/api/meetings/${meetingId}/plaud-imports`, {
      method: 'POST', body: JSON.stringify({ recordingId }),
    }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['plaud-recordings'] }),
        client.invalidateQueries({ queryKey: ['plaud-imports', meetingId] }),
        client.invalidateQueries({ queryKey: ['meeting-files', meetingId] }),
        client.invalidateQueries({ queryKey: ['meeting-tree'] }),
      ]);
      setOpen(false);
    },
  });
  const confirmSummary = useMutation({
    mutationFn: (importId: string) => api(`/api/meetings/${meetingId}/plaud-imports/${importId}/confirm-summary`, { method: 'POST' }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['plaud-imports', meetingId] }),
        client.invalidateQueries({ queryKey: ['meeting-tree'] }),
      ]);
    },
  });
  const removeImport = useMutation({
    mutationFn: (importId: string) => api(`/api/meetings/${meetingId}/plaud-imports/${importId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['plaud-imports', meetingId] }),
        client.invalidateQueries({ queryKey: ['meeting-files', meetingId] }),
      ]);
    },
  });
  const refreshSummary = useMutation({
    mutationFn: (importId: string) => api<{ data: PlaudImport }>(`/api/meetings/${meetingId}/plaud-imports/${importId}/refresh-summary`, { method: 'POST' }),
    onSuccess: async ({ data }) => {
      setRefreshMessage(`ตรวจสอบ Summary ล่าสุดแล้ว เมื่อ ${new Date().toLocaleTimeString('th-TH')}`);
      await Promise.all([
        client.invalidateQueries({ queryKey: ['plaud-imports', meetingId] }),
        client.invalidateQueries({ queryKey: ['plaud-recordings'] }),
        ...(data.summary ? [client.invalidateQueries({ queryKey: ['meeting-tree'] })] : []),
      ]);
    },
  });
  const mergeSummaries = useMutation({
    mutationFn: () => api(`/api/meetings/${meetingId}/plaud-imports/merge-summary`, { method: 'POST', body: JSON.stringify({ importIds: selectedSummaries, prompt: mergePrompt }) }),
    onSuccess: async () => { setSelectedSummaries([]); await client.invalidateQueries({ queryKey: ['meeting-tree'] }); },
  });

  return (
    <section className="mt-5 rounded-2xl border border-teal-200 bg-teal-50/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-bold">PLAUD Note</h3>
          <p className="text-xs text-slate-600">เลือกนำเข้าเสียง Transcript และ Summary จากบัญชีที่เชื่อมไว้บน NAS</p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => setOpen((value) => !value)}>
          {open ? 'ปิดรายการ' : 'นำเข้าจาก PLAUD'}
        </button>
      </div>

      {open && (
        <div className="mt-4 rounded-xl bg-white p-3">
          {recordings.isLoading && <p className="py-4 text-center text-sm text-slate-500">กำลังอ่านรายการจาก PLAUD…</p>}
          {recordings.error && <div className="space-y-2">
            <ErrorNotice message={recordings.error.message} />
            {recordings.error instanceof ApiClientError && ['PLAUD_AUTH_REQUIRED', 'PLAUD_NOT_CONNECTED'].includes(recordings.error.code) && (
              <p className="text-sm text-slate-700">ตรวจสถานะ token และสิทธิ์ไฟล์บน NAS ก่อนเข้าสู่ระบบ PLAUD ใหม่</p>
            )}
            <button type="button" className="btn-secondary" onClick={() => void recordings.refetch()} disabled={recordings.isFetching}>ตรวจการเชื่อมต่ออีกครั้ง</button>
            <button type="button" className="btn-secondary" onClick={() => void diagnosis.refetch()} disabled={diagnosis.isFetching}>{diagnosis.isFetching ? 'กำลังวินิจฉัย…' : 'วินิจฉัยสาเหตุ'}</button>
            {diagnosis.data && <p role="status" className="rounded-lg border p-3 text-sm text-slate-800">{plaudStatusMessage[diagnosis.data.status]}</p>}
            {diagnosis.error && <ErrorNotice message={diagnosis.error.message} />}
          </div>}
          <div className="divide-y divide-slate-100">
            {(recordings.data ?? []).map((recording) => (
              <div key={recording.id} className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{recording.name}</p>
                  <p className="text-xs text-slate-500">{date(recording.createdAt)} · {duration(recording.durationMs)}</p>
                </div>
                <button
                  type="button"
                  className="btn-secondary shrink-0"
                  disabled={importRecording.isPending}
                  onClick={() => importRecording.mutate(recording.id)}
                >
                  {importRecording.isPending ? 'กำลังนำเข้า…' : recording.imported ? 'นำเข้า Summary ใหม่' : 'เลือกนำเข้า'}
                </button>
                {recording.imported && (() => { const existing = (imports.data ?? []).find((item) => item.recordingId === recording.id); return existing ? <button type="button" className="btn-secondary shrink-0" onClick={() => refreshSummary.mutate(existing.id)} disabled={refreshSummary.isPending}>{refreshSummary.isPending ? 'กำลังตรวจ…' : 'เลือก/ตรวจ Summary ใหม่'}</button> : null; })()}
              </div>
            ))}
            {recordings.isSuccess && recordings.data.length === 0 && <p className="py-4 text-sm text-slate-500">ไม่พบ Recording ใน PLAUD</p>}
          </div>
          {importRecording.error && <ErrorNotice message={importRecording.error.message} />}
        </div>
      )}

      {(imports.data ?? []).map((item) => (
        <article key={item.id} className="mt-4 rounded-xl border border-teal-100 bg-white p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="font-semibold">{item.recordingName}</p>
              <p className="text-xs text-slate-500">{date(item.recordedAt)} · {duration(item.durationMs)}</p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${item.status === 'confirmed' ? 'bg-emerald-100 text-emerald-800' : item.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
              {item.status === 'confirmed' ? 'ยืนยันแล้ว' : item.status === 'failed' ? 'นำเข้าไม่สำเร็จ' : 'รอตรวจสอบ'}
            </span>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            {item.storedFileId ? 'มีไฟล์เสียงใน NAS' : 'ไม่มีไฟล์เสียง'} · {item.hasTranscript ? 'มี Transcript' : 'ไม่มี Transcript'}
          </p>
          {item.summary && (
            <div className="mt-3 rounded-xl bg-slate-50 p-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-teal-800"><input type="checkbox" className="h-4 w-4" checked={selectedSummaries.includes(item.id)} onChange={(event) => setSelectedSummaries((current) => event.target.checked ? [...current, item.id] : current.filter((id) => id !== item.id))} />เลือก Summary นี้เพื่อรวม</label>
              <p className="text-xs font-bold text-slate-600">Summary จาก PLAUD</p>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{item.summary}</p>
            </div>
          )}
          <button
            type="button"
            className="btn-secondary mt-3 w-full border-red-200 text-red-700 hover:bg-red-50"
            disabled={removeImport.isPending}
            onClick={() => {
              if (window.confirm('นำ Recording นี้ออกจากการประชุมหรือไม่? ไฟล์ที่นำเข้าและ Summary/Transcript จะถูกลบ แต่ต้นฉบับใน PLAUD จะไม่ถูกลบ')) {
                removeImport.mutate(item.id);
              }
            }}
          >
            {removeImport.isPending ? 'กำลังนำออก…' : 'นำออกจากการประชุม'}
          </button>
          {item.status === 'needs_review' && item.summary && (
            <button type="button" className="btn-primary mt-3 w-full" disabled={confirmSummary.isPending} onClick={() => confirmSummary.mutate(item.id)}>
              {confirmSummary.isPending ? 'กำลังยืนยัน…' : 'ยืนยันใช้ Summary นี้'}
            </button>
          )}
          <button
            type="button"
            className="btn-secondary mt-3 w-full"
            disabled={refreshSummary.isPending}
            onClick={() => refreshSummary.mutate(item.id)}
          >
            {refreshSummary.isPending ? 'กำลังตรวจ Summary จาก PLAUD…' : 'เลือก Summary ใหม่จาก PLAUD'}
          </button>
          {item.errorMessage && <p className="mt-2 text-xs text-red-700">{item.errorMessage}</p>}
        </article>
      ))}
      {refreshMessage && <p className="mt-3 rounded-lg bg-emerald-50 p-2 text-xs text-emerald-800">{refreshMessage}</p>}
      {selectedSummaries.length >= 2 && <div className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50 p-4"><p className="font-semibold text-indigo-900">รวม Summary ที่เลือกด้วย AI</p><p className="mt-1 text-xs text-indigo-700">ไฟล์เสียงจะไม่ถูกอัปโหลดซ้ำ ระบบใช้เฉพาะ Summary ที่เลือก</p><textarea className="input mt-3 min-h-24" value={mergePrompt} onChange={(event) => setMergePrompt(event.target.value)} aria-label="Prompt สำหรับรวม Summary" /><button type="button" className="btn-primary mt-3 w-full" onClick={() => mergeSummaries.mutate()} disabled={mergeSummaries.isPending}>{mergeSummaries.isPending ? 'กำลังรวมสรุป…' : `รวม ${selectedSummaries.length} Summary ด้วย AI`}</button></div>}
      {(imports.error || confirmSummary.error || removeImport.error || mergeSummaries.error || refreshSummary.error) && <ErrorNotice message={(imports.error ?? confirmSummary.error ?? removeImport.error ?? mergeSummaries.error ?? refreshSummary.error)?.message ?? 'จัดการข้อมูล PLAUD ไม่สำเร็จ'} />}
    </section>
  );
}
